import React, { useState, useEffect, useMemo } from "react";
import { Button } from "./components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./components/ui/dialog";
import { Checkbox } from "./components/ui/checkbox";
import { Progress } from "./components/ui/progress";
import { Calendar } from "./components/ui/calendar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  BookOpen,
  PenTool,
  Library,
  Menu,
  Crown,
  LogOut,
  User,
  Mail,
  Phone,
  Globe,
  Lock,
  FileText,
  TrendingUp,
  CalendarDays,
  Gamepad2,
  Heart,
  Users,
  Trophy,
  ExternalLink,
  Instagram,
  Quote,
  Lightbulb,
  Trash2,
  ArrowLeft,
} from "lucide-react";
import ProgresoView from "./components/ProgresoView";
import JuegosView from "./components/JuegosView";
import MotivacionView from "./components/MotivacionView";
import ConcursosView from "./components/ConcursosView";
import FragmentosView from "./components/FragmentosView";
import "./App.css";
import SubscriptionManagement from "./components/SubscriptionManagement";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="mt-auto border-t bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-3 py-3 text-xs sm:text-sm">
          {/* Izquierda */}
          <div className="text-gray-700 md:justify-self-start text-center md:text-left">
            <span className="font-semibold">Mi Minuto Narrativo</span>, una app
            de <span className="font-semibold">Carola Mía Ediciones</span>
          </div>

          {/* Centro */}
          <div className="text-gray-600 text-center md:justify-self-center">
            © {year} · Todos los derechos reservados
          </div>

          {/* Derecha (alineación perfecta) */}
          <div className="md:justify-self-end">
            <div className="flex items-center justify-center md:justify-end gap-2 h-6 sm:h-7">
              <a
                href="https://polkadev.es"
                target="_blank"
                rel="noreferrer"
                className="flex items-center hover:opacity-90 pb-2"
                aria-label="polkadev"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/polkadev-logo-black-marciano.png`}
                  alt="polkadev"
                  className="h-6 sm:h-7 w-auto object-contain"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// === HELPERS DE ACCESO (ADMIN / PREMIUM) ===
const normalize = (v) => (typeof v === "string" ? v.trim().toLowerCase() : v);

const isTruthy = (v) =>
  v === true || v === 1 || v === "1" || v === "true" || v === "True";

const getIsAdmin = (u) => {
  if (!u) return false;
  const role = normalize(u.rol ?? u.role ?? u.user_role);
  if (["admin", "administrator", "superadmin", "owner", "staff"].includes(role))
    return true;
  if (isTruthy(u.is_admin) || isTruthy(u.is_staff) || isTruthy(u.is_superuser))
    return true;
  if (Array.isArray(u.permissions)) {
    return u.permissions
      .map((p) => normalize(p))
      .some((p) => ["admin", "staff", "superuser"].includes(p));
  }
  return false;
};

const getIsPremium = (u) => {
  const t = normalize(u?.tipo_cuenta);
  return t === "premium" || isTruthy(u?.is_premium);
};

// === Helpers micro-lección (meta y tips) ===
const countWords = (s = "") =>
  s ? s.trim().split(/\s+/).filter(Boolean).length : 0;

const readingMins = (w) => Math.max(1, Math.round(w / 200)); // ~200 wpm

const makeStarters = (titulo = "") => [
  `Empieza con una imagen sensorial relacionada con “${titulo.toLowerCase()}”: `,
  `Abre con una acción en progreso que revele el “${titulo.toLowerCase()}”: `,
  `Plantea una pregunta que active el conflicto de “${titulo.toLowerCase()}”: `,
];

const consejoAvanzado = (titulo = "") => {
  const t = (titulo || "").toLowerCase();
  if (t.includes("diálogo"))
    return "Haz que cada línea cumpla dos funciones: avanzar la acción y revelar carácter. Suprime saludos y muletillas.";
  if (t.includes("personaj"))
    return "Regala una micro-obsesión observable (gesto, olor, número). Ancla la voz y evita generalidades.";
  if (t.includes("escena") || t.includes("ambiente"))
    return "Integra 1 detalle sensorial por párrafo; alterna sentidos. Evita listas: invierte causa-efecto para sorprender.";
  if (t.includes("gancho") || t.includes("inicio"))
    return "Usa contraste inmediato (cálido/frío, sagrado/profano). La incongruencia engancha.";
  if (t.includes("pov") || t.includes("narrador"))
    return "Limita el foco: prohíbe datos que tu narrador no percibe en ese instante.";
  if (t.includes("ritmo") || t.includes("tensión"))
    return "Para acelerar: frases cortas y verbos fuertes; para frenar: detalle concreto + comparación funcional.";
  return "Cierra con una consecuencia mínima pero visible. Que algo cambie (aunque sea una decisión).";
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const isAdmin = getIsAdmin(user);
  const isPremium = getIsPremium(user);
  const isPremiumOrAdmin = isAdmin || isPremium;
  const userLoaded = user !== null;
  const [currentView, setCurrentView] = useState("lecciones");
  const [lecciones, setLecciones] = useState([]);
  const [leccionActual, setLeccionActual] = useState(null);
  const [misEscritos, setMisEscritos] = useState([]);
  const [retos, setRetos] = useState([]);
  const [progreso, setProgreso] = useState(null);
  const [fragmentos, setFragmentos] = useState([]);
  const [juegos, setJuegos] = useState([]);
  const [fraseMotivadora, setFraseMotivadora] = useState(null);
  const [concursos, setConcursos] = useState([]);
  const [publicacionInfo, setPublicacionInfo] = useState(null);
  // const [comunidad, setComunidad] = useState([]);
  const [calendarioEventos, setCalendarioEventos] = useState([]);

  // Estados para pagos
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Estados para suscripciones
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Estados para ejercicios (antes "retos")
  const [showRetoModal, setShowRetoModal] = useState(false);
  const [retoActual, setRetoActual] = useState(null);
  const [retoForm, setRetoForm] = useState({ contenido: "" });

  // Estados para diario
  const [diarioEntradas, setDiarioEntradas] = useState([]);
  const [nuevaEntrada, setNuevaEntrada] = useState("");
  const [tituloEntrada, setTituloEntrada] = useState("");
  const [estadoAnimo, setEstadoAnimo] = useState("neutro");

  // Estados para formularios
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    nombre: "",
    apellido: "",
    email: "",
    telefono: "",
    pais: "",
    password: "",
    aceptaPolitica: false,
  });
  const [ejercicioTexto, setEjercicioTexto] = useState("");
  const [tituloEscrito, setTituloEscrito] = useState("");
  const [etiquetasEscrito, setEtiquetasEscrito] = useState("");

  // <-- NUEVO: controlar la pestaña de auth (login/register) para poder cambiarla desde el header -->
  const [authTab, setAuthTab] = useState("login");

  // Meta de lectura (concepto + ejemplo)
  const lecturaPalabras = useMemo(() => {
    if (!leccionActual) return 0;
    return (
      countWords(leccionActual.concepto) + countWords(leccionActual.ejemplo)
    );
  }, [leccionActual]);

  const lecturaMinutos = useMemo(
    () => readingMins(lecturaPalabras),
    [lecturaPalabras]
  );

  // Arranques sugeridos y progreso sugerido (150 palabras de referencia)
  const starters = useMemo(
    () => (leccionActual ? makeStarters(leccionActual.titulo) : []),
    [leccionActual]
  );
  const progresoSugerido = useMemo(() => {
    const w = countWords(ejercicioTexto);
    return Math.min(100, Math.round((w / 150) * 100));
  }, [ejercicioTexto]);

  useEffect(() => {
    if (token) {
      fetchUserProfile();
      fetchLecciones();
      fetchSubscription();
      fetchProgreso();
    }

    // Verificar si viene de un pago exitoso
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("payment") === "success") {
      setPaymentStatus({
        message: "¡Pago exitoso! Tu cuenta ahora es Premium",
        type: "success",
      });

      // Forzar actualización del usuario Y suscripción
      fetchUserProfile();
      fetchLecciones();
      fetchSubscription();

      // Limpiar la URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [token]);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  const fetchLecciones = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/lecciones`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLecciones(data.lecciones);
      }
    } catch (error) {
      console.error("Error fetching lecciones:", error);
    }
  };

  const fetchMisEscritos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mis-escritos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setMisEscritos(data.escritos);
      }
    } catch (error) {
      console.error("Error fetching escritos:", error);
    }
  };

  const fetchRetos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/retos-semanales`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRetos(data.retos);
      }
    } catch (error) {
      console.error("Error fetching retos:", error);
    }
  };

  const fetchProgreso = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mi-progreso`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProgreso(data);
      }
    } catch (error) {
      console.error("Error fetching progreso:", error);
    }
  };

  const fetchFragmentos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/biblioteca-fragmentos`);
      if (response.ok) {
        const data = await response.json();
        setFragmentos(data.fragmentos);
      }
    } catch (error) {
      console.error("Error fetching fragmentos:", error);
    }
  };

  const fetchJuegosForLeccion = async (leccionId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/juegos-creativos?leccion_id=${leccionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        return data.juegos;
      }
    } catch (error) {
      console.error("Error fetching juegos for leccion:", error);
    }
    return [];
  };

  const fetchJuegos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/juegos-creativos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setJuegos(data.juegos);
      }
    } catch (error) {
      console.error("Error fetching juegos:", error);
    }
  };

  const fetchFraseMotivadora = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/frase-motivadora`);
      if (response.ok) {
        const data = await response.json();
        setFraseMotivadora(data);
      }
    } catch (error) {
      console.error("Error fetching frase motivadora:", error);
    }
  };

  const fetchConcursos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/concursos-carola`);
      if (response.ok) {
        const data = await response.json();
        setConcursos(data);
      }
    } catch (error) {
      console.error("Error fetching concursos:", error);
    }
  };

  const fetchPublicacionInfo = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/publicacion-info`);
      if (response.ok) {
        const data = await response.json();
        setPublicacionInfo(data);
      }
    } catch (error) {
      console.error("Error fetching info publicacion:", error);
    }
  };

  // const fetchComunidad = async () => {
  //   try {
  //     const response = await fetch(`${BACKEND_URL}/api/comunidad`, {
  //       headers: { Authorization: `Bearer ${token}` },
  //     });
  //     if (response.ok) {
  //       const data = await response.json();
  //       setComunidad(data);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching comunidad:", error);
  //   }
  // };

  const fetchSubscription = async () => {
    if (!token) return;

    // Admin no necesita nada de suscripción
    if (getIsAdmin(user)) {
      setSubscription(null);
      setSubscriptionLoading(false);
      return;
    }

    setSubscriptionLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscription/details`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();

        // fallback si es premium manual sin Stripe
        if (getIsPremium(user) && !data.has_subscription) {
          setSubscription({
            has_subscription: true,
            status: "active",
            user_premium: true,
            is_manual: true,
          });
        } else {
          setSubscription(data);
        }
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
      if (getIsPremium(user)) {
        setSubscription({
          has_subscription: true,
          status: "active",
          user_premium: true,
          is_manual: true,
        });
      }
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // === FUNCIONES DE DIARIO ===

  const handleGuardarEntrada = () => {
    if (!nuevaEntrada.trim()) {
      alert("Escribe algo en tu diario antes de guardar");
      return;
    }

    const entrada = {
      id: Date.now().toString(),
      titulo:
        tituloEntrada ||
        `Entrada del ${new Date().toLocaleDateString("es-ES")}`,
      contenido: nuevaEntrada,
      estadoAnimo: estadoAnimo,
      fecha: new Date(),
      fechaTexto: new Date().toLocaleDateString("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    };

    const nuevasEntradas = [entrada, ...diarioEntradas];
    setDiarioEntradas(nuevasEntradas);

    localStorage.setItem("diario_entradas", JSON.stringify(nuevasEntradas));

    setNuevaEntrada("");
    setTituloEntrada("");
    setEstadoAnimo("neutro");

    alert("¡Entrada guardada en tu diario personal!");
  };

  const cargarEntradasDiario = () => {
    const entradasGuardadas = localStorage.getItem("diario_entradas");
    if (entradasGuardadas) {
      const entradas = JSON.parse(entradasGuardadas);
      const entradasConFechas = entradas.map((entrada) => ({
        ...entrada,
        fecha: new Date(entrada.fecha),
      }));
      setDiarioEntradas(entradasConFechas);
    }
  };

  const eliminarEntrada = (id) => {
    if (confirm("¿Estás seguro de que quieres eliminar esta entrada?")) {
      const nuevasEntradas = diarioEntradas.filter(
        (entrada) => entrada.id !== id
      );
      setDiarioEntradas(nuevasEntradas);
      localStorage.setItem("diario_entradas", JSON.stringify(nuevasEntradas));
    }
  };

  const getEmojiEstadoAnimo = (estado) => {
    const emojis = {
      "muy-feliz": "😄",
      feliz: "😊",
      neutro: "😐",
      triste: "😔",
      "muy-triste": "😢",
      inspirado: "✨",
      frustrado: "😤",
      emocionado: "🤗",
      reflexivo: "🤔",
    };
    return emojis[estado] || "😐";
  };

  useEffect(() => {
    if (currentView === "diario") {
      cargarEntradasDiario();
    }
  }, [currentView]);

  // === FUNCIONES DE PAGO ===

  const getUrlParameter = (name) => {
    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
    const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
    const results = regex.exec(window.location.search);
    return results === null
      ? ""
      : decodeURIComponent(results[1].replace(/\+/g, " "));
  };

  const updatePaymentStatus = (message, type) => {
    setPaymentStatus({ message, type });
    if (type === "success") {
      fetchUserProfile();
    }
  };

  useEffect(() => {
    const qp = new URLSearchParams(window.location.search);
    const sid = qp.get("session_id");
    if (!sid || !token) return;

    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/checkout/verify/${sid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        await res.json();

        await Promise.all([
          fetchSubscription(),
          fetchUserProfile(),
          fetchLecciones(),
        ]);
      } finally {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }
    })();
  }, [token]);

  const initiatePremiumPurchase = async () => {
    setPaymentLoading(true);
    setPaymentStatus({ message: "Redirigiendo a pago...", type: "pending" });

    try {
      const response = await fetch(`${BACKEND_URL}/api/checkout/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          package_id: "premium_annual",
          origin_url: window.location.origin,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Error al crear sesión de pago");
      }

      const data = await response.json();

      localStorage.setItem("last_stripe_session", data.session_id);

      window.location.href = data.url;
    } catch (error) {
      updatePaymentStatus(error.message, "error");
      console.error("Payment error:", error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const showPremiumModal = () => {
    setShowPaymentModal(true);
  };

  // === FUNCIONES DE EJERCICIOS (antes "retos") ===

  const handleParticiparReto = (reto) => {
    setRetoActual(reto);
    setRetoForm({ contenido: "" });
    setShowRetoModal(true);
  };

  const handleSubmitReto = async (e) => {
    e.preventDefault();

    if (!retoForm.contenido.trim()) {
      alert("Por favor escribe tu ejercicio");
      return;
    }

    const autoTitulo = `Ejercicio: ${retoActual?.titulo || "Práctica"} (Reto ${
      retoActual?.id
    })`;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/participar-reto/${retoActual.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            titulo: autoTitulo,
            contenido: retoForm.contenido,
            // Etiqueta coherente por si quieres verla en tu listado
            etiquetas: [`reto_${retoActual.id}`],
            // No es necesario para el backend de participar-reto, pero por consistencia:
            tipo: "reto",
          }),
        }
      );

      if (response.ok) {
        alert("¡Ejercicio enviado!");
        setShowRetoModal(false);
        setRetoForm({ contenido: "" });
        await fetchProgreso();
        setCurrentView("progreso");
      } else {
        const error = await response.json();
        alert(error.detail || "Error al enviar el ejercicio");
      }
    } catch (error) {
      console.error("Error enviando ejercicio:", error);
      alert("Error de conexión. Por favor intenta de nuevo.");
    }
  };

  const handleGuardarEnBibliotecaDesdeModal = async () => {
    if (!retoForm.contenido.trim()) {
      alert("Escribe tu ejercicio antes de guardar");
      return;
    }

    // Incluye “Reto {id}” en el título (fallback del backend)
    const autoTitulo = `Ejercicio: ${retoActual?.titulo || "Práctica"} (Reto ${
      retoActual?.id
    })`;

    try {
      const res = await fetch(`${BACKEND_URL}/api/guardar-escrito`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          titulo: autoTitulo,
          contenido: retoForm.contenido,
          // IMPORTANTE: etiqueta 'reto_{id}' para que el backend lo detecte si hiciera falta
          etiquetas: retoActual?.id ? [`reto_${retoActual.id}`] : [],
          // IMPORTANTE: tipo 'reto' para que el backend marque el progreso del reto
          tipo: "reto",
          // IMPORTANTE: pasa el reto_id explícito
          reto_id: String(retoActual?.id || ""),
        }),
      });

      if (res.ok) {
        alert("¡Guardado en tu biblioteca!");
        await Promise.all([fetchMisEscritos(), fetchProgreso()]);
        setShowRetoModal(false);
        setRetoForm({ contenido: "" });
        setCurrentView("progreso");
      } else {
        const error = await res.json();
        alert(error.detail || "No se pudo guardar el ejercicio");
      }
    } catch (err) {
      console.error("Error guardando en biblioteca:", err);
      alert("Error de conexión. Intenta de nuevo.");
    }
  };

  // === AUTH / OTROS ===

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!loginForm.email || !loginForm.password) {
      alert("Por favor, ingresa tu email y contraseña");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem("token", data.token);
        setLoginForm({ email: "", password: "" });
      } else {
        const error = await response.json();
        alert(error.detail || "Credenciales incorrectas");
      }
    } catch (error) {
      console.error("Error logging in:", error);
      alert("Error de conexión. Por favor intenta de nuevo.");
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (
      !registerForm.nombre ||
      !registerForm.apellido ||
      !registerForm.email ||
      !registerForm.telefono ||
      !registerForm.pais ||
      !registerForm.password
    ) {
      alert("Por favor, completa todos los campos");
      return;
    }

    if (!registerForm.aceptaPolitica) {
      alert("Debes aceptar la Política de Privacidad para continuar");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(registerForm),
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem("token", data.token);
        setRegisterForm({
          nombre: "",
          apellido: "",
          email: "",
          telefono: "",
          pais: "",
          password: "",
          aceptaPolitica: false,
        });
      } else {
        const error = await response.json();
        alert(error.detail || "Error al registrar usuario");
      }
    } catch (error) {
      console.error("Error registering:", error);
      alert("Error de conexión. Por favor intenta de nuevo.");
    }
  };

  const eliminarEscrito = async (escritoId) => {
    if (
      !window.confirm(
        "¿Estás seguro de que deseas eliminar este escrito? Esta acción no se puede deshacer."
      )
    ) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/escrito/${escritoId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMisEscritos(
          misEscritos.filter((escrito) => escrito.id !== escritoId)
        );
        await fetchProgreso();
        setCurrentView("progreso");

        alert("Escrito eliminado exitosamente");
      } else {
        const error = await response.json();
        alert(error.detail || "Error al eliminar el escrito");
      }
    } catch (error) {
      console.error("Error eliminando escrito:", error);
      alert("Error de conexión. Por favor intenta de nuevo.");
    }
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
    setCurrentView("lecciones");
  };

  const handleGuardarEjercicio = async () => {
    if (!ejercicioTexto.trim() || !tituloEscrito.trim()) {
      alert("Por favor, completa el título y el ejercicio");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/guardar-escrito`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          titulo: tituloEscrito,
          contenido: ejercicioTexto,
          etiquetas: etiquetasEscrito
            .split(",")
            .map((tag) => tag.trim())
            .filter((tag) => tag),
          tipo: "ejercicio",
          leccion_id: leccionActual?.id,
        }),
      });

      if (response.ok) {
        alert("¡Ejercicio guardado exitosamente!");
        setEjercicioTexto("");
        setTituloEscrito("");
        setEtiquetasEscrito("");

        // ⬇️ Actualiza biblioteca y progreso
        await Promise.all([fetchMisEscritos(), fetchProgreso()]);
        setCurrentView("progreso");
      }
    } catch (error) {
      console.error("Error guardando ejercicio:", error);
      alert("Error al guardar el ejercicio");
    }
  };

  const handleUpgradePremium = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/upgrade-premium`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        alert("¡Cuenta actualizada a premium!");
        await fetchUserProfile();
        await fetchLecciones();
      }
    } catch (error) {
      console.error("Error upgrading to premium:", error);
    }
  };

  const cancelSubscription = async () => {
    if (
      !window.confirm(
        "¿Estás seguro de que quieres cancelar tu suscripción?\n\nTendrás acceso hasta el final del período actual."
      )
    ) {
      return;
    }

    setSubscriptionLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/cancel`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "No se pudo cancelar");

      alert(data.message || "Suscripción cancelada");
      await Promise.all([
        fetchSubscription(),
        fetchUserProfile(),
        fetchLecciones(),
      ]);
    } catch (e) {
      alert(e.message);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const reactivateSubscription = async () => {
    if (!token) return;
    setSubscriptionLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/reactivate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 404) {
          alert(
            (data.detail ||
              "No se encontró una suscripción activa en Stripe.") +
              "\n\nPulsa Aceptar para volver a suscribirte."
          );
          setShowPaymentModal(true);
          return;
        }
        throw new Error(data.detail || "No se pudo reactivar la suscripción.");
      }

      alert(
        "¡Suscripción reactivada! La renovación automática vuelve a estar activa."
      );
      await Promise.all([
        fetchSubscription(),
        fetchUserProfile(),
        fetchLecciones(),
      ]);
    } catch (error) {
      console.error("Error reactivating subscription:", error);
      alert(error.message || "Error al reactivar la suscripción");
    } finally {
      setSubscriptionLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-dvh flex flex-col bg-gradient-to-br bg-[#e7cebf]">
        {/* Header igual que la app (solo logo cuando no hay sesión) */}
        <header className="sticky top-0 z-40 bg-white/95 shadow-sm border-b backdrop-blur supports-[backdrop-filter]:bg-white/80">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center">
              <img
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt="Mi Minuto Narrativo"
                className="h-8 md:h-12 w-auto select-none"
                width={128}
                height={128}
                decoding="async"
                loading="eager"
                draggable="false"
              />
              <span className="sr-only">Mi Minuto Narrativo</span>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <span className="text-sm text-gray-600">¿Nuevo aquí?</span>
              <a
                href="#register"
                onClick={(e) => {
                  e.preventDefault();
                  // Cambia a la pestaña "register"
                  setAuthTab("register");
                  // Espera a que el contenido se monte y desplaza + enfoca
                  setTimeout(() => {
                    const section = document.getElementById("register");
                    if (section) {
                      section.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                      const focusable = section.querySelector(
                        "input, select, textarea, button"
                      );
                      if (focusable && focusable.focus) {
                        focusable.focus();
                      }
                    }
                  }, 60);
                }}
                className="text-[#28364a] hover:underline font-medium"
              >
                Crea tu cuenta
              </a>
            </div>
          </div>
        </header>

        {/* Decoración sutil moderna */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          <div className="absolute -top-24 -left-24 h-64 w-64 rounded-full bg-white/40 blur-3xl opacity-60" />
          <div className="absolute bottom-[-6rem] right-[-6rem] h-72 w-72 rounded-full bg-white/30 blur-3xl opacity-60" />
        </div>

        <main className="container mx-auto px-4 py-10 flex-1">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Columna izquierda: hero / copy */}
            <section className="text-center lg:text-left space-y-4 lg:self-start">
              <h1 className="text-4xl md:text-5xl font-bold text-[#28364a] tracking-tight">
                HAZ DE LA ESCRITURA UN HÁBITO PARA CREAR HISTORIAS
              </h1>
              <p className="text-sm md:text-base text-gray-800/90">
                Tu camino ameno y práctico hacia la escritura creativa.
              </p>
              <p className="text-sm md:text-base text-gray-800/90">
                Una app multiplataforma para escritores de todos los niveles.
              </p>
              <ul className="text-sm md:text-base text-gray-700 space-y-2">
                <li>• Micro-lecciones para avanzar sin agobios</li>
                <li>• Ejercicios prácticos con seguimiento de progreso</li>
                <li>• Biblioteca personal y calendario de escritura</li>
              </ul>
            </section>

            {/* Columna derecha: Card de auth con glass/blur y tabs mejores */}
            <section>
              <div className="max-w-md mx-auto">
                {/* <-- Tabs controladas por estado authTab --> */}
                <Tabs
                  value={authTab}
                  onValueChange={setAuthTab}
                  className="w-full"
                  id="tabs-auth"
                >
                  <Card className="border-none shadow-xl bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
                    {/* Tabs integrados en el header del Card */}
                    <CardHeader className="pb-2">
                      <TabsList className="grid w-full grid-cols-2 rounded-xl bg-white/70 backdrop-blur border p-1">
                        <TabsTrigger
                          value="login"
                          data-value="login"
                          className="rounded-lg data-[state=active]:bg-[#28364a] data-[state=active]:text-white"
                        >
                          Iniciar Sesión
                        </TabsTrigger>
                        <TabsTrigger
                          value="register"
                          data-value="register"
                          className="rounded-lg data-[state=active]:bg-[#28364a] data-[state=active]:text-white"
                        >
                          Registrarse
                        </TabsTrigger>
                      </TabsList>
                    </CardHeader>

                    {/* Contenidos de cada tab dentro del mismo Card */}
                    <CardContent className="pt-0">
                      {/* LOGIN */}
                      <TabsContent value="login" className="m-0">
                        <form onSubmit={handleLogin} className="space-y-4">
                          <div>
                            <Label htmlFor="email" className="mb-1 block">
                              Email
                            </Label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                id="email"
                                type="email"
                                value={loginForm.email}
                                onChange={(e) =>
                                  setLoginForm({
                                    ...loginForm,
                                    email: e.target.value,
                                  })
                                }
                                placeholder="tu@email.com"
                                required
                                className="pl-9"
                              />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="password" className="mb-1 block">
                              Contraseña
                            </Label>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                id="password"
                                type="password"
                                value={loginForm.password}
                                onChange={(e) =>
                                  setLoginForm({
                                    ...loginForm,
                                    password: e.target.value,
                                  })
                                }
                                placeholder="••••••••"
                                required
                                className="pl-9"
                              />
                            </div>
                          </div>

                          <Button
                            type="submit"
                            className="w-full bg-[#28364a] hover:bg-[#344761]"
                          >
                            Iniciar Sesión
                          </Button>

                          <p className="text-xs text-gray-600 text-center">
                            Al continuar, aceptas nuestra{" "}
                            <Dialog>
                              <DialogTrigger asChild>
                                <button
                                  type="button"
                                  className="text-[#28364a] hover:underline"
                                >
                                  Política de Privacidad
                                </button>
                              </DialogTrigger>
                              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle className="flex items-center gap-2">
                                    <FileText className="h-5 w-5" />
                                    Política de Privacidad - Mi Minuto Narrativo
                                  </DialogTitle>
                                  <DialogDescription>
                                    Información sobre el tratamiento de tus
                                    datos personales
                                  </DialogDescription>
                                </DialogHeader>

                                {/* CONTENIDO COMPLETO */}
                                <div className="text-sm text-gray-700 space-y-4">
                                  <div>
                                    <h3 className="font-semibold text-lg mb-2">
                                      1. Responsable del tratamiento
                                    </h3>
                                    <p>
                                      <strong>Responsable:</strong> Carola Mía
                                      Ediciones (Alejandra López Arévalo)
                                    </p>
                                    <p>
                                      <strong>NIF:</strong> 05206574H
                                    </p>
                                    <p>
                                      <strong>Domicilio social:</strong> C/
                                      Cepas, 9 - 28270, Colmenarejo, Madrid
                                    </p>
                                    <p>
                                      <strong>Correo electrónico:</strong>{" "}
                                      info@carolamiaediciones.es
                                    </p>
                                    <p>
                                      <strong>Sitio web:</strong>{" "}
                                      <a
                                        href="http://www.carolamiaediciones.es"
                                        className="text-amber-600 hover:underline"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        www.carolamiaediciones.es
                                      </a>
                                    </p>
                                  </div>

                                  <div>
                                    <h3 className="font-semibold text-lg mb-2">
                                      2. Finalidad del tratamiento
                                    </h3>
                                    <p>
                                      Los datos personales recabados serán
                                      utilizados para gestionar el acceso y uso
                                      de la app Mi Minuto Narrativo, permitir la
                                      creación del perfil de usuario y, si se
                                      consiente expresamente, enviar boletines o
                                      comunicaciones comerciales.
                                    </p>
                                  </div>

                                  <div>
                                    <h3 className="font-semibold text-lg mb-2">
                                      3. Legitimación
                                    </h3>
                                    <p>
                                      La base legal es el consentimiento del
                                      usuario al registrarse en la app y aceptar
                                      esta política.
                                    </p>
                                  </div>

                                  <div>
                                    <h3 className="font-semibold text-lg mb-2">
                                      4. Conservación de los datos
                                    </h3>
                                    <p>
                                      Se conservarán mientras el usuario
                                      mantenga su cuenta activa o no solicite su
                                      supresión. Posteriormente se conservarán
                                      bloqueados según la legislación vigente.
                                    </p>
                                  </div>

                                  <div>
                                    <h3 className="font-semibold text-lg mb-2">
                                      5. Destinatarios
                                    </h3>
                                    <p>
                                      No se cederán datos a terceros salvo
                                      obligación legal. No se realizarán
                                      transferencias internacionales.
                                    </p>
                                  </div>

                                  <div>
                                    <h3 className="font-semibold text-lg mb-2">
                                      6. Derechos del usuario
                                    </h3>
                                    <p>
                                      El usuario puede acceder, rectificar,
                                      suprimir, oponerse, limitar y portar sus
                                      datos, así como retirar su consentimiento.
                                      Para ello puede escribir a
                                      info@carolamiaediciones.es adjuntando
                                      copia de su DNI.
                                    </p>
                                    <p className="mt-2">
                                      También puede presentar una reclamación
                                      ante la Agencia Española de Protección de
                                      Datos:{" "}
                                      <a
                                        href="http://www.aepd.es"
                                        className="text-amber-600 hover:underline"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        www.aepd.es
                                      </a>
                                    </p>
                                  </div>

                                  <div>
                                    <h3 className="font-semibold text-lg mb-2">
                                      7. Seguridad
                                    </h3>
                                    <p>
                                      Se aplican medidas técnicas y
                                      organizativas para garantizar la
                                      seguridad, confidencialidad e integridad
                                      de los datos.
                                    </p>
                                  </div>

                                  <div>
                                    <h3 className="font-semibold text-lg mb-2">
                                      8. Cambios en la política
                                    </h3>
                                    <p>
                                      Esta política podrá modificarse. Se
                                      notificará cualquier cambio mediante la
                                      app o correo electrónico.
                                    </p>
                                  </div>

                                  <div>
                                    <h3 className="font-semibold text-lg mb-2">
                                      9. Reglamento General de protección de
                                      datos (RGPD)
                                    </h3>
                                    <p>
                                      Reglamento (UE) 2016/679, L.O. 3/2018.
                                    </p>
                                    <p className="mt-2">
                                      <strong>Última actualización:</strong>{" "}
                                      agosto de 2025
                                    </p>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </p>
                        </form>
                      </TabsContent>

                      {/* REGISTER */}
                      <TabsContent value="register" className="m-0">
                        <form
                          onSubmit={handleRegister}
                          className="space-y-4 scroll-mt-28"
                          id="register"
                        >
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="nombre">Nombre</Label>
                              <Input
                                id="nombre"
                                value={registerForm.nombre}
                                onChange={(e) =>
                                  setRegisterForm({
                                    ...registerForm,
                                    nombre: e.target.value,
                                  })
                                }
                                placeholder="Juan"
                                required
                              />
                            </div>
                            <div>
                              <Label htmlFor="apellido">Apellido</Label>
                              <Input
                                id="apellido"
                                value={registerForm.apellido}
                                onChange={(e) =>
                                  setRegisterForm({
                                    ...registerForm,
                                    apellido: e.target.value,
                                  })
                                }
                                placeholder="Pérez"
                                required
                              />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="email-reg">Email</Label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                id="email-reg"
                                type="email"
                                value={registerForm.email}
                                onChange={(e) =>
                                  setRegisterForm({
                                    ...registerForm,
                                    email: e.target.value,
                                  })
                                }
                                placeholder="tu@email.com"
                                required
                                className="pl-9"
                              />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="telefono">Teléfono</Label>
                            <div className="relative">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                id="telefono"
                                value={registerForm.telefono}
                                onChange={(e) =>
                                  setRegisterForm({
                                    ...registerForm,
                                    telefono: e.target.value,
                                  })
                                }
                                placeholder="+34 600 123 456"
                                required
                                className="pl-9"
                              />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="pais">País</Label>
                            <div className="relative">
                              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                id="pais"
                                list="paises"
                                value={registerForm.pais}
                                onChange={(e) =>
                                  setRegisterForm({
                                    ...registerForm,
                                    pais: e.target.value,
                                  })
                                }
                                placeholder="Selecciona o escribe tu país"
                                required
                                className="pl-9"
                              />
                            </div>
                            <datalist id="paises">
                              <option value="España" />
                              <option value="México" />
                              <option value="Argentina" />
                              <option value="Colombia" />
                              <option value="Chile" />
                              <option value="Perú" />
                              <option value="Venezuela" />
                              <option value="Ecuador" />
                              <option value="Uruguay" />
                              <option value="Paraguay" />
                              <option value="Bolivia" />
                              <option value="República Dominicana" />
                              <option value="Costa Rica" />
                              <option value="Guatemala" />
                              <option value="Honduras" />
                              <option value="El Salvador" />
                              <option value="Nicaragua" />
                              <option value="Panamá" />
                              <option value="Cuba" />
                              <option value="Puerto Rico" />
                            </datalist>
                          </div>

                          <div>
                            <Label htmlFor="password-reg">Contraseña</Label>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                id="password-reg"
                                type="password"
                                value={registerForm.password}
                                onChange={(e) =>
                                  setRegisterForm({
                                    ...registerForm,
                                    password: e.target.value,
                                  })
                                }
                                placeholder="••••••••"
                                required
                                className="pl-9"
                              />
                            </div>
                          </div>

                          {/* Política de Privacidad */}
                          <div className="space-y-3">
                            <div className="flex items-start space-x-2">
                              <Checkbox
                                id="politica"
                                checked={registerForm.aceptaPolitica}
                                onCheckedChange={(checked) =>
                                  setRegisterForm({
                                    ...registerForm,
                                    aceptaPolitica: checked,
                                  })
                                }
                                required
                              />
                              <div className="text-sm">
                                <label
                                  htmlFor="politica"
                                  className="cursor-pointer"
                                >
                                  Acepto la{" "}
                                  <Dialog>
                                    <DialogTrigger asChild>
                                      <button
                                        type="button"
                                        className="text-[#28364a] hover:underline font-medium"
                                      >
                                        Política de Privacidad
                                      </button>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                                      <DialogHeader>
                                        <DialogTitle className="flex items-center gap-2">
                                          <FileText className="h-5 w-5" />
                                          Política de Privacidad - Mi Minuto
                                          Narrativo
                                        </DialogTitle>
                                        <DialogDescription>
                                          Información sobre el tratamiento de
                                          tus datos personales
                                        </DialogDescription>
                                      </DialogHeader>

                                      {/* CONTENIDO COMPLETO */}
                                      <div className="text-sm text-gray-700 space-y-4">
                                        <div>
                                          <h3 className="font-semibold text-lg mb-2">
                                            1. Responsable del tratamiento
                                          </h3>
                                          <p>
                                            <strong>Responsable:</strong> Carola
                                            Mía Ediciones (Alejandra López
                                            Arévalo)
                                          </p>
                                          <p>
                                            <strong>NIF:</strong> 05206574H
                                          </p>
                                          <p>
                                            <strong>Domicilio social:</strong>{" "}
                                            C/ Cepas, 9 - 28270, Colmenarejo,
                                            Madrid
                                          </p>
                                          <p>
                                            <strong>Correo electrónico:</strong>{" "}
                                            info@carolamiaediciones.es
                                          </p>
                                          <p>
                                            <strong>Sitio web:</strong>{" "}
                                            <a
                                              href="http://www.carolamiaediciones.es"
                                              className="text-amber-600 hover:underline"
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              www.carolamiaediciones.es
                                            </a>
                                          </p>
                                        </div>

                                        <div>
                                          <h3 className="font-semibold text-lg mb-2">
                                            2. Finalidad del tratamiento
                                          </h3>
                                          <p>
                                            Los datos personales recabados serán
                                            utilizados para gestionar el acceso
                                            y uso de la app Mi Minuto Narrativo,
                                            permitir la creación del perfil de
                                            usuario y, si se consiente
                                            expresamente, enviar boletines o
                                            comunicaciones comerciales.
                                          </p>
                                        </div>

                                        <div>
                                          <h3 className="font-semibold text-lg mb-2">
                                            3. Legitimación
                                          </h3>
                                          <p>
                                            La base legal es el consentimiento
                                            del usuario al registrarse en la app
                                            y aceptar esta política.
                                          </p>
                                        </div>

                                        <div>
                                          <h3 className="font-semibold text-lg mb-2">
                                            4. Conservación de los datos
                                          </h3>
                                          <p>
                                            Se conservarán mientras el usuario
                                            mantenga su cuenta activa o no
                                            solicite su supresión.
                                            Posteriormente se conservarán
                                            bloqueados según la legislación
                                            vigente.
                                          </p>
                                        </div>

                                        <div>
                                          <h3 className="font-semibold text-lg mb-2">
                                            5. Destinatarios
                                          </h3>
                                          <p>
                                            No se cederán datos a terceros salvo
                                            obligación legal. No se realizarán
                                            transferencias internacionales.
                                          </p>
                                        </div>

                                        <div>
                                          <h3 className="font-semibold text-lg mb-2">
                                            6. Derechos del usuario
                                          </h3>
                                          <p>
                                            El usuario puede acceder,
                                            rectificar, suprimir, oponerse,
                                            limitar y portar sus datos, así como
                                            retirar su consentimiento. Para ello
                                            puede escribir a
                                            info@carolamiaediciones.es
                                            adjuntando copia de su DNI.
                                          </p>
                                          <p className="mt-2">
                                            También puede presentar una
                                            reclamación ante la Agencia Española
                                            de Protección de Datos:{" "}
                                            <a
                                              href="http://www.aepd.es"
                                              className="text-amber-600 hover:underline"
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              www.aepd.es
                                            </a>
                                          </p>
                                        </div>

                                        <div>
                                          <h3 className="font-semibold text-lg mb-2">
                                            7. Seguridad
                                          </h3>
                                          <p>
                                            Se aplican medidas técnicas y
                                            organizativas para garantizar la
                                            seguridad, confidencialidad e
                                            integridad de los datos.
                                          </p>
                                        </div>

                                        <div>
                                          <h3 className="font-semibold text-lg mb-2">
                                            8. Cambios en la política
                                          </h3>
                                          <p>
                                            Esta política podrá modificarse. Se
                                            notificará cualquier cambio mediante
                                            la app o correo electrónico.
                                          </p>
                                        </div>

                                        <div>
                                          <h3 className="font-semibold text-lg mb-2">
                                            9. Reglamento General de protección
                                            de datos (RGPD)
                                          </h3>
                                          <p>
                                            Reglamento (UE) 2016/679, L.O.
                                            3/2018.
                                          </p>
                                          <p className="mt-2">
                                            <strong>
                                              Última actualización:
                                            </strong>{" "}
                                            agosto de 2025
                                          </p>
                                        </div>
                                      </div>
                                    </DialogContent>
                                  </Dialog>{" "}
                                  y el tratamiento de mis datos personales
                                </label>
                              </div>
                            </div>
                          </div>

                          <Button
                            type="submit"
                            className="w-full bg-[#28364a] hover:bg-[#344761]"
                          >
                            Crear Cuenta
                          </Button>
                        </form>
                      </TabsContent>
                    </CardContent>
                  </Card>
                </Tabs>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col bg-gradient-to-br bg-[#e7cebf] relative">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 shadow-sm border-b backdrop-blur supports-[backdrop-filter]:bg-white/80">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center">
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="Mi Minuto Narrativo"
              className="h-8 md:h-12 w-auto select-none"
              width={128}
              height={128}
              decoding="async"
              loading="eager"
              draggable="false"
            />
            <span className="sr-only">Mi Minuto Narrativo</span>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-700">
                {user?.nombre} {user?.apellido}
              </span>
              {isAdmin ? (
                <Badge
                  variant="secondary"
                  className="bg-indigo-100 text-indigo-800"
                >
                  Admin
                </Badge>
              ) : user?.tipo_cuenta === "premium" ? (
                <Badge
                  variant="secondary"
                  className="bg-yellow-100 text-yellow-800"
                >
                  <Crown className="h-3 w-3 mr-1" /> Premium
                </Badge>
              ) : (
                <Badge variant="outline">Gratis</Badge>
              )}
            </div>

            {userLoaded && !isPremiumOrAdmin && (
              <Button
                size="sm"
                className="bg-yellow-500 hover:bg-yellow-600"
                onClick={showPremiumModal}
              >
                <Crown className="h-4 w-4 mr-1" />
                Actualizar a Premium
              </Button>
            )}

            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-1" />
              Salir
            </Button>
          </div>

          {/* Mobile menu */}
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-64">
                <div className="flex flex-col gap-4 mt-6">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-700">
                      {user?.nombre} {user?.apellido}
                    </span>
                    {isAdmin ? (
                      <Badge
                        variant="secondary"
                        className="bg-indigo-100 text-indigo-800"
                      >
                        Admin
                      </Badge>
                    ) : user?.tipo_cuenta === "premium" ? (
                      <Badge
                        variant="secondary"
                        className="bg-yellow-100 text-yellow-800"
                      >
                        <Crown className="h-3 w-3 mr-1" /> Premium
                      </Badge>
                    ) : (
                      <Badge variant="outline">Gratis</Badge>
                    )}
                  </div>

                  {userLoaded && !isPremiumOrAdmin && (
                    <Button
                      size="sm"
                      className="bg-yellow-500 hover:bg-yellow-600 w-full"
                      onClick={showPremiumModal}
                    >
                      <Crown className="h-4 w-4 mr-1" />
                      Actualizar a Premium
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4 mr-1" />
                    Salir
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col">
        {/* Notificaciones de Estado de Pago */}
        {paymentStatus && (
          <div
            className={`sticky top-24 z-30 border-l-4 p-4 mx-4 mt-4 ${
              paymentStatus.type === "success"
                ? "bg-green-50 border-green-400 text-green-700"
                : paymentStatus.type === "error"
                ? "bg-red-50 border-red-400 text-red-700"
                : "bg-blue-50 border-blue-400 text-blue-700"
            }`}
          >
            <div className="flex justify-between items-start">
              <p className="font-medium">{paymentStatus.message}</p>
              <button
                onClick={() => setPaymentStatus(null)}
                className="ml-4 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <div className="lg:w-64 lg:sticky lg:top-24 lg:self-start">
              <nav className="space-y-2 lg:max-h-[calc(100vh-8rem)] lg:overflow-auto pr-1">
                <Button
                  variant="ghost"
                  className={`w-full justify-start ${
                    currentView === "suscripcion"
                      ? "bg-[#28364a] hover:bg-[#28364a] text-white hover:text-white"
                      : "bg-transparent hover:bg-transparent text-gray-900 hover:text-gray-900"
                  }`}
                  aria-current={
                    currentView === "suscripcion" ? "page" : undefined
                  }
                  onClick={() => {
                    setCurrentView("suscripcion");
                    fetchSubscription();
                  }}
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Mi suscripción
                </Button>

                <Button
                  variant="ghost"
                  className={`w-full justify-start ${
                    currentView === "lecciones"
                      ? "bg-[#28364a] hover:bg-[#28364a] text-white hover:text-white"
                      : "bg-transparent hover:bg-transparent text-gray-900 hover:text-gray-900"
                  }`}
                  aria-current={
                    currentView === "lecciones" ? "page" : undefined
                  }
                  onClick={() => setCurrentView("lecciones")}
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Micro-lecciones
                </Button>

                <Button
                  variant="ghost"
                  className={`w-full justify-start ${
                    currentView === "retos"
                      ? "bg-[#28364a] hover:bg-[#28364a] text-white hover:text-white"
                      : "bg-transparent hover:bg-transparent text-gray-900 hover:text-gray-900"
                  }`}
                  aria-current={currentView === "retos" ? "page" : undefined}
                  onClick={() => {
                    setCurrentView("retos");
                    fetchRetos();
                  }}
                >
                  <PenTool className="h-4 w-4 mr-2" />
                  Ejercicios prácticos
                  {userLoaded && !isPremiumOrAdmin && (
                    <Lock className="h-3 w-3 ml-auto" />
                  )}
                </Button>

                <Button
                  variant="ghost"
                  className={`w-full justify-start ${
                    currentView === "progreso"
                      ? "bg-[#28364a] hover:bg-[#28364a] text-white hover:text-white"
                      : "bg-transparent hover:bg-transparent text-gray-900 hover:text-gray-900"
                  }`}
                  aria-current={currentView === "progreso" ? "page" : undefined}
                  onClick={() => {
                    setCurrentView("progreso");
                    fetchProgreso();
                  }}
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Mi Progreso
                </Button>

                <Button
                  variant="ghost"
                  className={`w-full justify-start ${
                    currentView === "calendario"
                      ? "bg-[#28364a] hover:bg-[#28364a] text-white hover:text-white"
                      : "bg-transparent hover:bg-transparent text-gray-900 hover:text-gray-900"
                  }`}
                  aria-current={
                    currentView === "calendario" ? "page" : undefined
                  }
                  onClick={() => setCurrentView("calendario")}
                >
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Calendario
                </Button>

                <Button
                  variant="ghost"
                  className={`w-full justify-start ${
                    currentView === "diario"
                      ? "bg-[#28364a] hover:bg-[#28364a] text-white hover:text-white"
                      : "bg-transparent hover:bg-transparent text-gray-900 hover:text-gray-900"
                  }`}
                  aria-current={currentView === "diario" ? "page" : undefined}
                  onClick={() => setCurrentView("diario")}
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Mi diario
                </Button>

                <Button
                  variant="ghost"
                  className={`w-full justify-start ${
                    currentView === "biblioteca"
                      ? "bg-[#28364a] hover:bg-[#28364a] text-white hover:text-white"
                      : "bg-transparent hover:bg-transparent text-gray-900 hover:text-gray-900"
                  }`}
                  aria-current={
                    currentView === "biblioteca" ? "page" : undefined
                  }
                  onClick={() => {
                    setCurrentView("biblioteca");
                    fetchMisEscritos();
                  }}
                >
                  <Library className="h-4 w-4 mr-2" />
                  Mi Biblioteca
                </Button>

                <Button
                  variant="ghost"
                  className={`w-full justify-start ${
                    currentView === "fragmentos"
                      ? "bg-[#28364a] hover:bg-[#28364a] text-white hover:text-white"
                      : "bg-transparent hover:bg-transparent text-gray-900 hover:text-gray-900"
                  }`}
                  aria-current={
                    currentView === "fragmentos" ? "page" : undefined
                  }
                  onClick={() => {
                    setCurrentView("fragmentos");
                    fetchFragmentos();
                  }}
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Biblioteca Clásicos
                </Button>

                <Button
                  variant="ghost"
                  className={`w-full justify-start ${
                    currentView === "motivacion"
                      ? "bg-[#28364a] hover:bg-[#28364a] text-white hover:text-white"
                      : "bg-transparent hover:bg-transparent text-gray-900 hover:text-gray-900"
                  }`}
                  aria-current={
                    currentView === "motivacion" ? "page" : undefined
                  }
                  onClick={() => {
                    setCurrentView("motivacion");
                    fetchFraseMotivadora();
                  }}
                >
                  <Heart className="h-4 w-4 mr-2" />
                  Inspiración
                </Button>

                {/* <Button
                variant={currentView === "comunidad" ? "default" : "ghost"}
                className="w-full justify-start"
                onClick={() => {
                  setCurrentView("comunidad");
                  fetchComunidad();
                }}
              >
                <Users className="h-4 w-4 mr-2" />
                Mi Comunidad
                {userLoaded && !isPremiumOrAdmin && (
                  <Lock className="h-3 w-3 ml-auto" />
                )}
              </Button> */}

                <Button
                  variant={currentView === "concursos" ? "default" : "ghost"}
                  className={`w-full justify-start hover:bg-[#28364a] ${
                    currentView === "concursos"
                      ? "bg-[#28364a] text-white"
                      : "bg-transparent hover:bg-transparent"
                  }`}
                  aria-current={
                    currentView === "concursos" ? "page" : undefined
                  }
                  onClick={() => {
                    setCurrentView("concursos");
                    fetchConcursos();
                  }}
                >
                  <Trophy className="h-4 w-4 mr-2" />
                  Concursos Carola Mía
                </Button>

                {/* Publica con Nosotros */}
                <Button
                  variant="ghost"
                  className={`w-full justify-start ${
                    currentView === "publicar"
                      ? "bg-[#28364a] hover:bg-[#28364a] text-white hover:text-white"
                      : "bg-transparent hover:bg-transparent text-gray-900 hover:text-gray-900"
                  }`}
                  aria-current={currentView === "publicar" ? "page" : undefined}
                  onClick={() => {
                    setCurrentView("publicar");
                    fetchPublicacionInfo();
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Publica con Nosotros
                </Button>
              </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1">
              {currentView === "suscripcion" && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      Mi suscripción
                    </h2>
                    <p className="text-gray-600">
                      Gestiona tu plan y la renovación automática.
                    </p>
                  </div>

                  {subscriptionLoading ? (
                    <Card>
                      <CardContent className="py-8 text-center">
                        Cargando…
                      </CardContent>
                    </Card>
                  ) : isAdmin ? (
                    <Card className="max-w-xl">
                      <CardHeader>
                        <CardTitle>Acceso de administrador</CardTitle>
                        <CardDescription>
                          Tienes todo desbloqueado por ser admin.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="text-sm text-gray-600">
                        No necesitas gestionar suscripciones.
                      </CardContent>
                    </Card>
                  ) : isPremiumOrAdmin ? (
                    <SubscriptionManagement
                      isAdmin={isAdmin}
                      subscription={subscription}
                      onCancel={cancelSubscription}
                      onReactivate={reactivateSubscription}
                      loading={subscriptionLoading}
                    />
                  ) : (
                    <Card className="max-w-xl">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Crown className="h-5 w-5 text-yellow-500" />
                          Suscripción Premium Anual
                        </CardTitle>
                        <CardDescription>
                          Acceso completo a MI MINUTO NARRATIVO
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-center">
                          <div className="text-3xl font-bold text-yellow-600 mb-2">
                            €14.99
                          </div>
                          <div className="text-sm text-gray-600">
                            Anual • Renovación automática
                          </div>
                        </div>

                        <ul className="text-sm text-gray-700 space-y-2">
                          <li>• 80 lecciones premium completas</li>
                          <li>• Ejercicios prácticos exclusivos (semanales)</li>
                          <li>• Calendario de escritura avanzado</li>
                        </ul>

                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={showPremiumModal}
                          >
                            Ver detalles del plan
                          </Button>
                          <Button
                            className="flex-1 bg-yellow-500 hover:bg-yellow-600"
                            onClick={() => {
                              showPremiumModal();
                            }}
                            disabled={paymentLoading}
                          >
                            {paymentLoading ? "Procesando…" : "Suscribirme"}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {currentView === "lecciones" && (
                <div>
                  {leccionActual ? (
                    (() => {
                      // Helpers locales
                      const wc = (s = "") =>
                        (s.trim().match(/\S+/g) || []).length;
                      const lecturaPalabras =
                        wc(leccionActual?.concepto || "") +
                        wc(leccionActual?.ejemplo || "");
                      const lecturaMinutos = Math.max(
                        1,
                        Math.round(lecturaPalabras / 200)
                      ); // ~200 wpm

                      const consejoAvanzado = (titulo = "") => {
                        const t = (titulo || "").toLowerCase();
                        if (t.includes("diálogo"))
                          return "Haz que cada línea cumpla dos funciones: avanzar la acción y revelar carácter. Suprime saludos y muletillas.";
                        if (t.includes("personaj"))
                          return "Regala una micro-obsesión observable (gesto, olor, número). Ancla la voz y evita generalidades.";
                        if (t.includes("escena") || t.includes("ambiente"))
                          return "Integra 1 detalle sensorial por párrafo; alterna sentidos. Evita listas: invierte causa-efecto para sorprender.";
                        if (t.includes("gancho") || t.includes("inicio"))
                          return "Usa contraste inmediato (cálido/frío, sagrado/profano). La incongruencia engancha.";
                        if (t.includes("pov") || t.includes("narrador"))
                          return "Limita el foco: prohíbe datos que tu narrador no percibe en ese instante.";
                        if (t.includes("ritmo") || t.includes("tensión"))
                          return "Para acelerar: frases cortas y verbos fuertes; para frenar: detalle concreto + comparación funcional.";
                        return "Cierra con una consecuencia mínima pero visible. Que algo cambie (aunque sea una decisión).";
                      };

                      const starters = [
                        `Empieza con una imagen sensorial relacionada con “${(
                          leccionActual?.titulo || ""
                        ).toLowerCase()}”: `,
                        `Abre con una acción en progreso que revele el “${(
                          leccionActual?.titulo || ""
                        ).toLowerCase()}”: `,
                        `Plantea una pregunta que active el conflicto de “${(
                          leccionActual?.titulo || ""
                        ).toLowerCase()}”: `,
                      ];

                      const palabrasAlumno = wc(ejercicioTexto);
                      const progresoSugerido = Math.min(
                        100,
                        Math.round((palabrasAlumno / 150) * 100)
                      ); // objetivo soft: 150

                      return (
                        <Card>
                          <CardHeader>
                            <div className="flex justify-between items-start">
                              <div>
                                <CardTitle className="text-2xl">
                                  {leccionActual.titulo}
                                </CardTitle>
                                <CardDescription>
                                  Lección {leccionActual.id}
                                </CardDescription>

                                {/* Meta rápida (lectura) */}
                                <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                                  <span>⏱️ {lecturaMinutos} min</span>
                                  <span>•</span>
                                  <span>~{lecturaPalabras} palabras</span>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                onClick={() => setLeccionActual(null)}
                                className="group bg-[#28364a]/10 hover:bg-[#28364a]/15 text-[#28364a] border-[#28364a]/20 font-medium"
                                aria-label="Volver a micro-lecciones"
                              >
                                <ArrowLeft className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-0.5" />
                                Volver a micro-lecciones
                              </Button>
                            </div>
                          </CardHeader>

                          <CardContent className="space-y-6">
                            {leccionActual.bloqueada && !isPremiumOrAdmin ? (
                              <div className="text-center py-8">
                                <Lock className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                                <h3 className="text-xl font-semibold mb-2">
                                  Contenido Premium
                                </h3>
                                <p className="text-gray-600 mb-4">
                                  Esta lección está disponible solo para
                                  usuarios premium
                                </p>
                                <Button
                                  onClick={initiatePremiumPurchase}
                                  className="bg-yellow-500 hover:bg-yellow-600"
                                >
                                  <Crown className="h-4 w-4 mr-2" />
                                  Actualizar a Premium - €14.99
                                </Button>
                              </div>
                            ) : (
                              <>
                                <div>
                                  <h3 className="text-lg font-semibold mb-2">
                                    Concepto
                                  </h3>
                                  <p className="text-gray-700">
                                    {leccionActual.concepto}
                                  </p>
                                </div>

                                <div>
                                  <h3 className="text-lg font-semibold mb-2">
                                    Ejemplo
                                  </h3>
                                  <div className="bg-gray-50 p-4 rounded-lg italic">
                                    {leccionActual.ejemplo}
                                  </div>
                                </div>

                                {/* Consejo avanzado / Premium */}
                                {isPremiumOrAdmin ? (
                                  <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Lightbulb className="h-4 w-4 text-amber-600" />
                                      <span className="font-medium text-amber-900">
                                        Consejo avanzado
                                      </span>
                                    </div>
                                    <p className="text-sm text-amber-900">
                                      {consejoAvanzado(leccionActual.titulo)}
                                    </p>
                                  </div>
                                ) : (
                                  <div className="bg-gray-50 border p-3 rounded-lg text-xs text-gray-600 flex items-center justify-between">
                                    <span className="flex items-center">
                                      <Lock className="h-3 w-3 mr-1" />
                                      Consejo avanzado para esta lección
                                    </span>
                                    <Button
                                      size="sm"
                                      className="bg-yellow-500 hover:bg-yellow-600"
                                      onClick={showPremiumModal}
                                    >
                                      <Crown className="h-3 w-3 mr-1" /> Ver
                                    </Button>
                                  </div>
                                )}

                                {/* Arranques rápidos */}
                                <div className="mt-2">
                                  <h4 className="text-sm font-medium mb-2">
                                    Arranques rápidos
                                  </h4>
                                  <div className="flex flex-wrap gap-2">
                                    {starters.map((s, i) => (
                                      <Button
                                        key={i}
                                        size="sm"
                                        variant="outline"
                                        onClick={() =>
                                          setEjercicioTexto(
                                            (t) => (t ? t + "\n\n" : "") + s
                                          )
                                        }
                                      >
                                        {i === 0
                                          ? "Imagen"
                                          : i === 1
                                          ? "Acción"
                                          : "Pregunta"}
                                      </Button>
                                    ))}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() =>
                                        setEjercicioTexto(
                                          (t) =>
                                            (t ? t + "\n\n" : "") +
                                            (leccionActual?.ejemplo || "")
                                        )
                                      }
                                    >
                                      <Quote className="h-3 w-3 mr-1" /> Usar
                                      ejemplo
                                    </Button>
                                  </div>
                                </div>

                                <div>
                                  <h3 className="text-lg font-semibold mb-2">
                                    Tu turno
                                  </h3>
                                  <p className="text-gray-700 mb-4">
                                    {leccionActual.ejercicio}
                                  </p>

                                  <div className="space-y-4">
                                    <div>
                                      <Label htmlFor="titulo">
                                        Título de tu escrito
                                      </Label>
                                      <Input
                                        id="titulo"
                                        value={tituloEscrito}
                                        onChange={(e) =>
                                          setTituloEscrito(e.target.value)
                                        }
                                        placeholder="Ej: Mi primer gancho narrativo"
                                      />
                                    </div>

                                    <div>
                                      <Label htmlFor="ejercicio">
                                        Escribe tu respuesta
                                      </Label>
                                      <Textarea
                                        id="ejercicio"
                                        value={ejercicioTexto}
                                        onChange={(e) =>
                                          setEjercicioTexto(e.target.value)
                                        }
                                        placeholder="Escribe tu ejercicio aquí..."
                                        rows={6}
                                      />

                                      {/* Contador + Progreso */}
                                      <div className="text-xs text-gray-500 mt-1 flex items-center justify-between">
                                        <span>{palabrasAlumno} palabras</span>
                                        <span>Sugerido: 120–200</span>
                                      </div>
                                      <Progress
                                        value={progresoSugerido}
                                        className="mt-2"
                                      />
                                    </div>

                                    <div>
                                      <Label htmlFor="etiquetas">
                                        Etiquetas (separadas por comas)
                                      </Label>
                                      <Input
                                        id="etiquetas"
                                        value={etiquetasEscrito}
                                        onChange={(e) =>
                                          setEtiquetasEscrito(e.target.value)
                                        }
                                        placeholder="gancho, primera línea, ejercicio"
                                      />
                                    </div>

                                    <Button
                                      onClick={handleGuardarEjercicio}
                                      className="w-full bg-[#28364a] hover:bg-[#344761]"
                                    >
                                      Guardar Ejercicio
                                    </Button>
                                  </div>
                                </div>
                              </>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })()
                  ) : (
                    <div>
                      <div className="mb-6">
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">
                          Micro-lecciones
                        </h2>
                        <p className="text-gray-600">
                          Aprende técnicas narrativas en pequeñas dosis
                        </p>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {lecciones.map((leccion) => {
                          const bloqueada =
                            leccion.bloqueada &&
                            userLoaded &&
                            !isPremiumOrAdmin;

                          return (
                            <Card
                              key={leccion.id}
                              className="cursor-pointer hover:shadow-lg transition-shadow flex flex-col h-full"
                            >
                              <CardHeader className="flex-shrink-0">
                                <div className="flex justify-between items-start">
                                  <CardTitle className="text-lg pr-2">
                                    {leccion.titulo}
                                  </CardTitle>
                                  <div className="flex space-x-1 flex-shrink-0">
                                    {leccion.es_gratuita ? (
                                      <Badge
                                        variant="secondary"
                                        className="bg-green-100 text-green-800 text-xs"
                                      >
                                        Gratis
                                      </Badge>
                                    ) : (
                                      <Badge
                                        variant="outline"
                                        className="bg-yellow-100 text-yellow-800 text-xs"
                                      >
                                        <Crown className="h-3 w-3 mr-1" />{" "}
                                        Premium
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <CardDescription>
                                  Lección {leccion.id}
                                </CardDescription>
                              </CardHeader>

                              <CardContent className="flex-1 flex flex-col">
                                <div className="flex-1 mb-4">
                                  <p className="text-sm text-gray-600">
                                    {bloqueada
                                      ? "Actualiza a premium para acceder a esta lección"
                                      : leccion.concepto.substring(0, 100) +
                                        "..."}
                                  </p>
                                </div>

                                <div className="mt-auto">
                                  {bloqueada ? (
                                    <Button
                                      size="sm"
                                      className="w-full bg-yellow-500 hover:bg-yellow-600 text-white"
                                      onClick={showPremiumModal}
                                    >
                                      <Crown className="h-4 w-4 mr-2" /> Premium
                                      €14.99
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      className="w-full bg-[#28364a] hover:bg-[#344761]"
                                      onClick={() => setLeccionActual(leccion)}
                                    >
                                      Comenzar lección
                                    </Button>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {currentView === "retos" && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      Ejercicios prácticos
                    </h2>
                    <p className="text-gray-600">
                      Desafía tu creatividad con ejercicios únicos
                    </p>
                  </div>

                  {!isPremiumOrAdmin ? (
                    <Card>
                      <CardContent className="text-center py-8">
                        <Lock className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">
                          Contenido Premium
                        </h3>
                        <p className="text-gray-600 mb-4">
                          Los Ejercicios prácticos están disponibles solo para
                          usuarios premium
                        </p>
                        <Button
                          onClick={initiatePremiumPurchase}
                          className="bg-yellow-500 hover:bg-yellow-600"
                        >
                          <Crown className="h-4 w-4 mr-2" />
                          Actualizar a Premium - €14.99
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2 items-stretch">
                      {retos.map((reto) => (
                        <Card
                          key={reto.id}
                          className="h-full flex flex-col cursor-pointer hover:shadow-lg transition-shadow"
                        >
                          <CardHeader className="flex-none">
                            <CardTitle className="flex items-center gap-2">
                              <PenTool className="h-5 w-5 text-blue-600" />
                              {reto.titulo}
                            </CardTitle>
                            <CardDescription className="text-gray-600">
                              {reto.descripcion}
                            </CardDescription>
                          </CardHeader>

                          {/* El contenido se vuelve una columna flexible*/}
                          <CardContent className="flex-1 flex flex-col gap-4">
                            <div className="flex-1 space-y-4">
                              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                                  <BookOpen className="h-4 w-4" />
                                  Instrucciones
                                </h4>
                                <p className="text-blue-800 text-sm">
                                  {reto.instrucciones}
                                </p>
                              </div>

                              {reto.inspiracion && (
                                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                                  <h4 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                                    <Heart className="h-4 w-4" />
                                    Inspiración
                                  </h4>
                                  <p className="text-purple-800 text-sm italic">
                                    {reto.inspiracion}
                                  </p>
                                </div>
                              )}
                            </div>

                            <div className="mt-auto">
                              <Button
                                onClick={() => handleParticiparReto(reto)}
                                className="w-full bg-blue-600 hover:bg-blue-700"
                              >
                                <PenTool className="h-4 w-4 mr-2" />
                                Comenzar el ejercicio
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {currentView === "biblioteca" && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      Mi Biblioteca
                    </h2>
                    <p className="text-gray-600">
                      Todos tus escritos guardados en un solo lugar
                    </p>
                  </div>

                  {misEscritos.length === 0 ? (
                    <Card>
                      <CardContent className="text-center py-8">
                        <Library className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">
                          No tienes escritos guardados
                        </h3>
                        <p className="text-gray-600 mb-4">
                          Completa algunos ejercicios para ver tus escritos aquí
                        </p>
                        <Button onClick={() => setCurrentView("lecciones")}>
                          Ir a lecciones
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      {misEscritos.map((escrito) => (
                        <Card key={escrito.id}>
                          <CardHeader>
                            <div className="flex justify-between items-start">
                              <div>
                                <CardTitle className="text-lg">
                                  {escrito.titulo}
                                </CardTitle>
                                <CardDescription>
                                  {new Date(
                                    escrito.fecha_creacion
                                  ).toLocaleDateString("es-ES")}{" "}
                                  • {escrito.tipo}
                                </CardDescription>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => eliminarEscrito(escrito.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <p className="text-gray-700 mb-4 line-clamp-3">
                              {escrito.contenido.substring(0, 150)}...
                            </p>
                            <div className="flex flex-wrap gap-1 mb-4">
                              {escrito.etiquetas.map((etiqueta, index) => (
                                <Badge
                                  key={index}
                                  variant="outline"
                                  className="text-xs"
                                >
                                  {etiqueta}
                                </Badge>
                              ))}
                            </div>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full"
                                >
                                  Ver completo
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl">
                                <DialogHeader>
                                  <DialogTitle>{escrito.titulo}</DialogTitle>
                                  <DialogDescription>
                                    {new Date(
                                      escrito.fecha_creacion
                                    ).toLocaleDateString("es-ES")}
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="max-h-96 overflow-y-auto">
                                  <p className="whitespace-pre-wrap">
                                    {escrito.contenido}
                                  </p>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {currentView === "progreso" && (
                <ProgresoView
                  progreso={progreso}
                  user={user}
                  isAdmin={isAdmin}
                  onGoLecciones={() => setCurrentView("lecciones")}
                  onGoEjercicios={() => {
                    setCurrentView("retos");
                    fetchRetos();
                  }}
                  onGoBiblioteca={() => {
                    setCurrentView("biblioteca");
                    fetchMisEscritos();
                  }}
                />
              )}

              {currentView === "calendario" && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      Calendario de Escritura
                    </h2>
                    <p className="text-gray-600">
                      Planifica tus sesiones de escritura mensual
                    </p>
                  </div>

                  {!isPremiumOrAdmin ? (
                    <Card>
                      <CardContent className="text-center py-8">
                        <CalendarDays className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">
                          Calendario Premium
                        </h3>
                        <p className="text-gray-600 mb-4">
                          Accede al calendario de escritura mensual para
                          organizar tus sesiones y seguir tu progreso diario.
                        </p>
                        <Button onClick={initiatePremiumPurchase}>
                          <Crown className="h-4 w-4 mr-2" />
                          Actualizar a Premium
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid gap-6 md:grid-cols-2">
                      <Card>
                        <CardHeader>
                          <CardTitle>
                            {new Date()
                              .toLocaleDateString("es-ES", {
                                year: "numeric",
                                month: "long",
                              })
                              .replace(/^\w/, (c) => c.toUpperCase())}
                          </CardTitle>
                          <CardDescription>
                            Eventos de escritura planificados
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <Calendar />
                          <div className="mt-4 space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span>
                                {new Date(
                                  Date.now() + 3 * 24 * 60 * 60 * 1000
                                ).toLocaleDateString("es-ES", {
                                  day: "numeric",
                                  month: "long",
                                })}{" "}
                                - Microrrelato del día
                              </span>
                              <Badge variant="outline">Próximo</Badge>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span>
                                {new Date(
                                  Date.now() + 7 * 24 * 60 * 60 * 1000
                                ).toLocaleDateString("es-ES", {
                                  day: "numeric",
                                  month: "long",
                                })}{" "}
                                - Taller de diálogos
                              </span>
                              <Badge variant="outline">Programado</Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <div className="space-y-4">
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">
                              Próximos Eventos
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              <div className="border-l-4 border-blue-500 pl-4">
                                <div className="font-medium">
                                  Ejercicio de Personajes
                                </div>
                                <div className="text-sm text-gray-600">
                                  {new Date(
                                    Date.now() + 10 * 24 * 60 * 60 * 1000
                                  ).toLocaleDateString("es-ES", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  })}
                                </div>
                              </div>
                              <div className="border-l-4 border-green-500 pl-4">
                                <div className="font-medium">
                                  Escritura Libre
                                </div>
                                <div className="text-sm text-gray-600">
                                  {new Date(
                                    Date.now() + 15 * 24 * 60 * 60 * 1000
                                  ).toLocaleDateString("es-ES", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  })}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">
                              Mi Actividad
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-green-600 mb-1">
                                {Math.floor(Math.random() * 10) + 5}
                              </div>
                              <div className="text-sm text-gray-600 mb-3">
                                días activos este mes
                              </div>
                              <Progress value={70} className="w-full" />
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {currentView === "diario" && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      Mi Diario Personal
                    </h2>
                    <p className="text-gray-600">
                      Tu espacio privado para reflexiones, pensamientos y
                      progreso como escritor
                    </p>
                  </div>

                  {/* Formulario para nueva entrada */}
                  <Card className="mb-6 border-purple-200 bg-purple-50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-purple-900">
                        <PenTool className="h-5 w-5" />
                        Nueva Entrada
                      </CardTitle>
                      <CardDescription className="text-purple-700">
                        Comparte tus pensamientos, sentimientos o reflexiones
                        sobre tu proceso creativo
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="titulo-entrada">
                          Título (opcional)
                        </Label>
                        <Input
                          id="titulo-entrada"
                          value={tituloEntrada}
                          onChange={(e) => setTituloEntrada(e.target.value)}
                          placeholder="Dale un título a tu entrada..."
                          className="bg-white"
                        />
                      </div>

                      <div>
                        <Label htmlFor="estado-animo">
                          ¿Cómo te sientes hoy?
                        </Label>
                        <select
                          id="estado-animo"
                          value={estadoAnimo}
                          onChange={(e) => setEstadoAnimo(e.target.value)}
                          className="w-full p-2 border rounded-md bg-white"
                        >
                          <option value="muy-feliz">😄 Muy feliz</option>
                          <option value="feliz">😊 Feliz</option>
                          <option value="emocionado">🤗 Emocionado</option>
                          <option value="inspirado">✨ Inspirado</option>
                          <option value="reflexivo">🤔 Reflexivo</option>
                          <option value="neutro">😐 Neutro</option>
                          <option value="frustrado">😤 Frustrado</option>
                          <option value="triste">😔 Triste</option>
                          <option value="muy-triste">😢 Muy triste</option>
                        </select>
                      </div>

                      <div>
                        <Label htmlFor="entrada-contenido">Tu entrada</Label>
                        <Textarea
                          id="entrada-contenido"
                          value={nuevaEntrada}
                          onChange={(e) => setNuevaEntrada(e.target.value)}
                          placeholder="Escribe aquí tus pensamientos, reflexiones, lo que has escrito hoy, cómo te sientes sobre tu progreso creativo, ideas para futuras historias..."
                          rows={6}
                          className="bg-white"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          {nuevaEntrada.length} caracteres
                        </div>
                      </div>

                      <Button
                        onClick={handleGuardarEntrada}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        <BookOpen className="h-4 w-4 mr-2" />
                        Guardar en mi diario
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Lista de entradas */}
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      Mis Entradas ({diarioEntradas.length})
                    </h3>

                    {diarioEntradas.length === 0 ? (
                      <Card>
                        <CardContent className="text-center py-8">
                          <BookOpen className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                          <h3 className="text-xl font-semibold mb-2 text-gray-600">
                            Tu diario está esperándote
                          </h3>
                          <p className="text-gray-500">
                            Escribe tu primera entrada para comenzar a
                            documentar tu viaje como escritor
                          </p>
                        </CardContent>
                      </Card>
                    ) : (
                      diarioEntradas.map((entrada) => (
                        <Card
                          key={entrada.id}
                          className="hover:shadow-lg transition-shadow"
                        >
                          <CardHeader>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <CardTitle className="text-lg text-purple-900">
                                  {entrada.titulo}
                                </CardTitle>
                                <CardDescription className="flex items-center gap-2 mt-1">
                                  <span>
                                    {getEmojiEstadoAnimo(entrada.estadoAnimo)}
                                  </span>
                                  <span className="capitalize">
                                    {entrada.estadoAnimo.replace("-", " ")}
                                  </span>
                                  <span>•</span>
                                  <span>{entrada.fechaTexto}</span>
                                </CardDescription>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => eliminarEntrada(entrada.id)}
                                className="text-red-600 hover:bg-red-50"
                              >
                                Eliminar
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="prose max-w-none">
                              <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                                {entrada.contenido}
                              </p>
                            </div>
                            <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
                              Escrito el {entrada.fecha.toLocaleString("es-ES")}
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </div>
              )}

              {currentView === "fragmentos" && (
                <FragmentosView fragmentos={fragmentos} />
              )}

              {currentView === "motivacion" && (
                <MotivacionView
                  frase={fraseMotivadora}
                  onNuevaFrase={fetchFraseMotivadora}
                />
              )}

              {currentView === "concursos" && (
                <ConcursosView concursos={concursos} />
              )}

              {currentView === "publicar" && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      Publica con Nosotros
                    </h2>
                    <p className="text-gray-600">
                      Descubre cómo publicar tu obra con Carola Mía Ediciones
                    </p>
                  </div>

                  {publicacionInfo ? (
                    <div className="space-y-6">
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <ExternalLink className="h-5 w-5 text-blue-600" />
                            {publicacionInfo.titulo}
                          </CardTitle>
                          <CardDescription>
                            {publicacionInfo.descripcion}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid md:grid-cols-1 gap-6">
                            <div>
                              <h4 className="font-semibold mb-3">Requisitos</h4>
                              <ul className="space-y-2">
                                {publicacionInfo.requisitos.map(
                                  (requisito, index) => (
                                    <li
                                      key={index}
                                      className="flex items-center gap-2 text-sm"
                                    >
                                      <FileText className="h-4 w-4 text-blue-600" />
                                      {requisito}
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          </div>

                          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                            <h4 className="font-semibold mb-3">Contacto</h4>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm">
                                <ExternalLink className="h-4 w-4 text-blue-600" />
                                <a
                                  href={`https://${publicacionInfo.contacto.web}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline"
                                >
                                  {publicacionInfo.contacto.web}
                                </a>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Instagram className="h-4 w-4 text-pink-600" />
                                <a
                                  href={`https://instagram.com/${publicacionInfo.contacto.instagram.replace(
                                    "@",
                                    ""
                                  )}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-pink-600 hover:underline"
                                >
                                  {publicacionInfo.contacto.instagram}
                                </a>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Mail className="h-4 w-4 text-green-600" />
                                <a
                                  href={`mailto:${publicacionInfo.contacto.email}`}
                                  className="text-green-600 hover:underline"
                                >
                                  {publicacionInfo.contacto.email}
                                </a>
                              </div>
                              <p className="text-sm text-gray-600 italic">
                                {publicacionInfo.contacto.nota}
                              </p>
                            </div>
                          </div>

                          <div className="mt-6">
                            <h4 className="font-semibold mb-3">
                              Proceso de Publicación
                            </h4>
                            <div className="space-y-3">
                              {publicacionInfo.proceso.map((paso, index) => (
                                <div
                                  key={index}
                                  className="flex items-center gap-3"
                                >
                                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                    {index + 1}
                                  </div>
                                  <span className="text-sm">{paso}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ) : (
                    <Card>
                      <CardContent className="text-center py-8">
                        <BookOpen className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">
                          Cargando información...
                        </h3>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      {/* MODAL PAGO / DETALLES PREMIUM */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent
          className="max-w-md"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              Suscripción Premium Anual
            </DialogTitle>
            <DialogDescription>
              Acceso completo a MI MINUTO NARRATIVO
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-center">
              <div className="text-3xl font-bold text-yellow-600 mb-2">
                €14.99
              </div>
              <div className="text-sm text-gray-600">
                Anual • Renovación automática
              </div>
            </div>

            <ul className="text-sm text-gray-700 space-y-2">
              <li>• 80 lecciones premium completas</li>
              <li>• Ejercicios prácticos exclusivos</li>
              <li>• Calendario de escritura avanzado</li>
            </ul>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowPaymentModal(false)}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-yellow-500 hover:bg-yellow-600"
                onClick={async () => {
                  setShowPaymentModal(false);
                  await initiatePremiumPurchase();
                }}
                disabled={paymentLoading || isPremiumOrAdmin}
              >
                {paymentLoading ? "Procesando…" : "Suscribirme"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* MODAL DE EJERCICIO PRÁCTICO */}
      <Dialog open={showRetoModal} onOpenChange={setShowRetoModal}>
        <DialogContent
          className="w-[92vw] max-w-2xl p-0 h-[92vh] flex flex-col
             overflow-hidden rounded-xl border shadow-lg"
        >
          <form onSubmit={handleSubmitReto} className="flex flex-col h-full">
            {/* Header */}
            <DialogHeader className="px-6 py-4 border-b bg-white shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <PenTool className="h-5 w-5 text-blue-600" />
                Ejercicio práctico: {retoActual?.titulo}
              </DialogTitle>
              {retoActual?.descripcion && (
                <DialogDescription>{retoActual.descripcion}</DialogDescription>
              )}
            </DialogHeader>

            {/* Cuerpo con scroll */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Tiempo sugerido:</span> Sin
                    límite (tómate tu tiempo)
                  </div>
                  <div>
                    <span className="font-medium">Palabras sugeridas:</span>{" "}
                    150-300 palabras
                  </div>
                  <div>
                    <span className="font-medium">Categoría:</span> Ejercicio
                    creativo
                  </div>
                  <div>
                    <span className="font-medium">Nivel:</span> Principiante
                    amigable
                  </div>
                </div>
                <div className="mt-3 p-2 bg-white rounded border">
                  <p className="text-xs text-gray-600 italic">
                    💡 Recuerda: Esto es para divertirte y practicar. No hay
                    respuestas incorrectas, solo creatividad en desarrollo.
                  </p>
                </div>
              </div>

              {/* ÚNICO CAMPO */}
              <div>
                <Label htmlFor="contenido-ejercicio">Tu ejercicio</Label>
                <Textarea
                  id="contenido-ejercicio"
                  value={retoForm.contenido}
                  onChange={(e) => setRetoForm({ contenido: e.target.value })}
                  placeholder="Escribe tu ejercicio aquí..."
                  className="min-h-40 max-h-72 resize-y"
                  required
                />
                <div className="text-xs text-gray-500 mt-1">
                  {
                    retoForm.contenido.trim().split(/\s+/).filter(Boolean)
                      .length
                  }{" "}
                  / {retoActual?.palabras_limite}
                  {retoActual?.palabras_limite ? " palabras" : " palabras"}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-white shrink-0">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowRetoModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGuardarEnBibliotecaDesdeModal}
                  className="flex-1"
                >
                  <Library className="h-4 w-4 mr-2" />
                  Guardar en mi biblioteca
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}

export default App;
